import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdEdit, MdOutlineSettings } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type { CellContextMenuEvent, GetContextMenuItemsParams, GridApi, MenuItemDef, RowNode } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, RefObject } from 'react'
import type { TApp, IClusterApp } from '~/types/cluster'
import type { MobileDeviceType } from '~/types/device-type'

import AppsListingTable from './AppsListingTable'
import ExploreTable from './ExploreTable'
import ListingModeSwitchButton from './ListingModeSwitchButton'
import MetricsBlock from './MetricsBlock'

import AddAppToClusterModal from '../modals/AddAppToClusterModal'
import CompanyContactModal from '../modals/CompanyContactModal'
import CreateClusterModal from '../modals/CreateClusterModal'
import EditAppTagsModal from '../modals/EditAppTagsModal'
import EditClusterModal from '../modals/EditModal'
import ScreenshotsModal from '../modals/ScreenshotsModal'

import Button from '~/components/Buttons/Button'
import ModalDialog from '~/components/ModalDialog'
import SkeletonLoader from '~/components/SkeletonLoader'
import { APPLICATION_HARDCODED_TAGS } from '~/const/clusters'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useRequiredParams } from '~/hooks/useRequiredParams'
import { useClustersContext } from '~/providers/Clusters'
import { useSearchParamsContext } from '~/providers/SearchParams'
import {
  keys as clustersKeys,
  useGetClusterQuery,
  useBatchAddToClusterMutation,
  useBatchDeleteFromClusterMutation,
  useTagsQuery,
  useSetTagsMutation,
  useClustersLightListQuery,
} from '~/queries/clusters'
import { valueComparator } from '~/utils/comparators'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ClusterDetails = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { dispatch: dispatchToClustersContext, state } = useClustersContext()

  const { deviceType, filterPreset, listingMode, searchElements, tags } = state

  const { dispatchToSearchParams } = useSearchParamsContext()

  const { clusterId } = useRequiredParams<{ clusterId: string }>()

  const appsGridRef = useRef<AgGridReact<IClusterApp>>(null)

  const candidatesGridRef = useRef<AgGridReact<IClusterApp>>(null)

  const [gridRef, setCurrentGridRef] = useState<RefObject<AgGridReact<IClusterApp>> | null>(null)

  const [selectedRows, setSelectedRows] = useState<IClusterApp[]>([])

  const [contactDetails, setContactDetails] = useState({ email: '', phone: '' })

  const [isAddAppToClusterModalOpen, setIsAddAppToClusterModalOpen] = useState(false)

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false)

  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  const [isCreateClusterModalOpen, setIsCreateClusterModalOpen] = useState(false)

  const [isEditAppTagsModalOpen, setIsEditAppTagsModalOpen] = useState(false)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const [newClusterFromAppStoreId, setNewClusterFromAppStoreId] = useState<string>()

  const [appsForTagsUpdate, setAppsForTagsUpdate] = useState<TApp[]>()

  const [appForScreenshots, setAppForScreenshots] = useState<IClusterApp>()

  /** Queries & Mutations */

  const { data: tagsData = [] } = useTagsQuery(clusterId)

  const { data: clusterData } = useGetClusterQuery(clusterId)

  const { data: clustersList } = useClustersLightListQuery()

  const { mutateAsync: batchAddToCluster } = useBatchAddToClusterMutation()

  const { mutateAsync: batchDeleteFromCluster } = useBatchDeleteFromClusterMutation()

  const { mutateAsync: setTags } = useSetTagsMutation()

  /** Effects */

  useDocumentTitle('Usage Clustering - Cluster details')

  useEffect(() => {
    switch (listingMode) {
      case 'cluster':
        setCurrentGridRef(appsGridRef)
        break
      case 'candidates':
        setCurrentGridRef(candidatesGridRef)
        break
      default:
        setCurrentGridRef(null)
    }

    setSelectedRows([])
  }, [listingMode])

  /** Handlers */

  const handleAddAppToClusterModalState = (isOpen: boolean) => () => setIsAddAppToClusterModalOpen(isOpen)

  const handleEditAppTagsModalState = (isOpen: boolean) => () => {
    if (!isOpen) setAppsForTagsUpdate(undefined)
    setIsEditAppTagsModalOpen(isOpen)
  }

  const handleEditClusterModalState = (isOpen: boolean) => () => setIsEditModalOpen(isOpen)

  const handleContactModalState = (isOpen: boolean) => () => setIsContactModalOpen(isOpen)

  const handleRowSelectionChanged = useCallback(
    (newSelectedRows: IClusterApp[]) => {
      setSelectedRows(newSelectedRows)
    },
    [setSelectedRows]
  )

  const handleEditFeatures = () => navigate(`/competitors/clusters/${clusterId}/features-manager`)

  const handleContactIconClick = (email: string, phone: string) => {
    setIsContactModalOpen(true)
    setContactDetails({ email, phone })
  }

  const handleCreateClusterModalState = (isOpen: boolean) => () => setIsCreateClusterModalOpen(isOpen)

  const createClusterFromStoreId = (storeId: string) => () => {
    setNewClusterFromAppStoreId(storeId)
    setIsCreateClusterModalOpen(true)
  }

  const addAppsToCluster = useCallback(
    ({
      apps,
      targetClusterId,
      targetClusterName,
    }: {
      apps: { deviceType: MobileDeviceType; isTarget: boolean; storeId: string }[]
      targetClusterId: string
      targetClusterName: string
    }) =>
      async () => {
        try {
          await batchAddToCluster({ clusterId: targetClusterId, items: apps })
          await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
          await queryClient.invalidateQueries({ queryKey: clustersKeys.clusterStats(clusterId) })
          await queryClient.invalidateQueries({ queryKey: clustersKeys.listAppsForClusters({ clusterId }) })
          if (listingMode === 'candidates') {
            queryClient.invalidateQueries({
              queryKey: clustersKeys.clusterCandidates(clusterId, {
                deviceType,
                searchElements,
              }),
            })
          }
          displayToast(`App(s) successfully added to cluster ${`"${targetClusterName}"` || ''}`, 'success')
        } catch (e) {
          captureException(e)
          displayToast('App(s) could not be added to cluster', 'error')
        }
      },
    [batchAddToCluster, clusterId, deviceType, listingMode, queryClient, searchElements]
  )

  const removeAppsFromCluster = useCallback(
    (
      {
        apps,
        targetClusterId,
        targetClusterName,
      }: {
        apps: { deviceType: MobileDeviceType | null; storeId: string }[]
        targetClusterId: string
        targetClusterName: string
      },
      gridApi: GridApi
    ) =>
      async () => {
        try {
          await batchDeleteFromCluster({ clusterId: targetClusterId, items: apps })
          await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
          await queryClient.invalidateQueries({ queryKey: clustersKeys.clusterStats(clusterId) })
          await queryClient.invalidateQueries({ queryKey: clustersKeys.listAppsForClusters({ clusterId }) })

          if (targetClusterId === clusterId) {
            gridApi.applyServerSideTransaction({
              remove: apps.map(({ storeId }) => ({ store_id: storeId })),
            })
          }

          displayToast(`App(s) successfully removed from cluster ${`"${targetClusterName}"` || ''}`, 'success')
        } catch (e) {
          captureException(e)
          displayToast('App(s) could not be removed from cluster', 'error')
        }
      },
    [batchDeleteFromCluster, clusterId, queryClient]
  )

  const searchCandidates = useCallback(
    ({ selectedRows, storeId }: { selectedRows: IClusterApp[]; storeId: string }) =>
      () => {
        const selectedRowsStoreIds = selectedRows.map(({ store_id }) => store_id)
        const storeIds = selectedRowsStoreIds.includes(storeId) ? selectedRowsStoreIds : [storeId]

        dispatchToClustersContext({ type: 'listingMode', value: 'candidates' })
        dispatchToClustersContext({ type: 'searchElements', value: storeIds })
        dispatchToSearchParams({ type: 'update', value: { listingMode: 'candidates' } })
        dispatchToSearchParams({ type: 'update', value: { 'searchElements[]': storeIds } })
      },
    [dispatchToClustersContext, dispatchToSearchParams]
  )

  const updateTags = useCallback(
    (selectedApps: TApp[]) => async () => {
      setIsEditAppTagsModalOpen(true)
      setAppsForTagsUpdate(selectedApps)
    },
    []
  )

  const handleEditAppTagsSuccess = ({
    isMulti,
    storeIds,
    tags,
  }: {
    isMulti: boolean
    storeIds: string[]
    tags: string[]
  }) => {
    if (gridRef?.current) {
      if (isMulti) {
        const rowNodesData: IClusterApp[] = []

        gridRef.current.api.forEachNode((node) => {
          if (node.data && storeIds.includes(node.data.store_id)) {
            rowNodesData.push({
              ...node.data,
              tags: node.data.tags ? [...new Set([...node.data.tags, ...tags])] : [...new Set([...tags])],
            })
          }
        })

        gridRef.current.api.applyServerSideTransaction({
          update: rowNodesData,
        })
      } else {
        const rowNode = gridRef.current.api.getRowNode(storeIds[0])

        if (rowNode) {
          gridRef.current.api.applyServerSideTransaction({
            update: [{ ...rowNode.data, tags }],
          })
        }
      }

      setIsEditAppTagsModalOpen(false)
      setAppsForTagsUpdate(undefined)
    }
  }

  const navigateToAppInfo = useCallback(
    (deviceType: MobileDeviceType, storeId: string, clusterId: string) => () =>
      navigate(`/competitors/clusters/${clusterId}/app-infos/${storeId}/device-type/${deviceType}`),
    [navigate]
  )

  const addTagToCluster = useCallback(
    ({ apps, clusterId, tag }: { apps: TApp[]; clusterId: string; tag: string }, gridApi: GridApi) =>
      async () => {
        try {
          for (const { deviceType, storeId, tags } of apps) {
            const newTags =
              tag === 'star'
                ? [...tags.filter((el) => el !== 'blacklisted'), tag]
                : tag === 'blacklisted'
                  ? [...tags.filter((el) => el !== 'star'), tag]
                  : [...tags, tag]

            await setTags({
              clusterId,
              deviceType,
              storeId,
              tags: newTags,
            })

            const rowNode = gridApi.getRowNode(storeId)

            if (rowNode) {
              gridApi.applyServerSideTransaction({
                update: [{ ...rowNode.data, tags: newTags }],
              })
            }
          }
          displayToast(`Tag "${tag}" successfully added`, 'success')
        } catch (e) {
          captureException(e)
          displayToast(`Tag "${tag}" could not be added`, 'error')
        }
      },
    [setTags]
  )

  const getAddTagActions = useCallback(
    (
      {
        apps,
        clusterId,
        isActionMulti,
      }: {
        apps: TApp[]
        clusterId: string
        isActionMulti: boolean
      },
      gridApi: GridApi
    ): MenuItemDef[] => {
      const menuItems: MenuItemDef[] = []

      for (const { icon, label, value } of APPLICATION_HARDCODED_TAGS) {
        if (
          (!isActionMulti && !apps[0].tags.includes(value)) ||
          (isActionMulti && !apps.every(({ tags }) => tags.includes(value)))
        ) {
          menuItems.push({
            action: addTagToCluster({ apps, clusterId, tag: value }, gridApi),
            cssClasses: ['menu-item'],
            icon,
            name: `Tag as "${label}"`,
          })
        }
      }

      return menuItems
    },
    [addTagToCluster]
  )

  const handleRemoveTagFromCluster = useCallback(
    async (value: { deviceType: string; storeId: string; tags: string[] }, gridApi: GridApi) => {
      try {
        await setTags({ clusterId, ...value })

        const rowNode = gridApi.getRowNode(value.storeId)

        if (rowNode) {
          gridApi.applyServerSideTransaction({
            update: [{ ...rowNode.data, tags: value.tags }],
          })
        }
      } catch (e) {
        captureException(e)
      }
    },
    [clusterId, setTags]
  )

  const getClustersMenuItems = useCallback(
    ({
      clusterIds,
      isActionMulti,
      selectedApps,
    }: {
      clusterIds: string[]
      isActionMulti: boolean
      selectedApps: TApp[]
    }) => {
      return (
        clustersList
          ?.filter(({ cluster_id }) => (isActionMulti ? cluster_id !== clusterId : !clusterIds.includes(cluster_id)))
          .sort((a, b) => valueComparator(a.name || '', b.name || ''))
          .map(
            ({ cluster_id, name }): MenuItemDef => ({
              action: addAppsToCluster({ apps: selectedApps, targetClusterId: cluster_id, targetClusterName: name }),
              cssClasses: ['menu-subitem'],
              name: name || 'Nameless cluster 🥷',
            })
          ) || []
      )
    },
    [clusterId, clustersList, addAppsToCluster]
  )

  const getRemoveFromClusterMenuItems = useCallback(
    (
      {
        clusterIds,
        isActionMulti,
        selectedApps,
      }: {
        clusterIds: string[]
        isActionMulti: boolean
        selectedApps: TApp[]
      },
      gridApi: GridApi
    ): MenuItemDef[] =>
      clustersList
        ?.filter(({ cluster_id }) =>
          isActionMulti
            ? [...new Set(selectedApps.map(({ clusters }) => clusters?.map(({ id }) => id)).flat())].includes(
                cluster_id
              )
            : clusterIds.includes(cluster_id)
        )
        .sort((a, b) => valueComparator(a.name || '', b.name || ''))
        .map(({ cluster_id, name }) => ({
          action: removeAppsFromCluster(
            { apps: selectedApps, targetClusterId: cluster_id, targetClusterName: name },
            gridApi
          ),
          cssClasses: ['menu-subitem'],
          name: name || 'Nameless cluster 🥷',
        })) || [],
    [clustersList, removeAppsFromCluster]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<IClusterApp>) => {
      const rowData = params.node?.data

      const selectedRows: IClusterApp[] = params.api.getSelectedRows()

      if (!rowData) return []

      const { clusters, device_type, is_target, store_id, tags } = rowData

      const isActionMulti = selectedRows.length > 1 && !!params.node?.isSelected()

      const clusterIds = clusters?.map(({ id }) => id) || []

      const selectedApps: TApp[] = isActionMulti
        ? selectedRows.map(({ clusters, device_type, is_target, store_id }) => ({
            clusters: clusters || [],
            deviceType: device_type,
            isTarget: !!is_target,
            storeId: store_id,
            tags: tags || [],
          }))
        : [
            {
              clusters: clusters || [],
              deviceType: device_type,
              isTarget: !!is_target,
              storeId: store_id,
              tags: tags || [],
            },
          ]

      const commonMenuItems: MenuItemDef[] = [
        ...(is_target !== null
          ? [
              {
                cssClasses: ['menu-item'],
                icon: '➕',
                name: 'Add to cluster',
                subMenu: getClustersMenuItems({ clusterIds, isActionMulti, selectedApps }),
              },
            ]
          : []),
        {
          action: createClusterFromStoreId(store_id),
          cssClasses: ['menu-item'],
          icon: '↗️',
          name: `New cluster from app${isActionMulti && selectedRows.length > 1 ? 's' : ''}`,
        },
        {
          action: searchCandidates({ selectedRows, storeId: store_id }),
          cssClasses: ['menu-item'],
          icon: '🔍',
          name: 'Use to search candidates',
        },
      ]

      const candidateMenuItems: MenuItemDef[] =
        is_target !== null
          ? [
              {
                action: addAppsToCluster({
                  apps: selectedApps,
                  targetClusterId: clusterId,
                  targetClusterName: clusterData?.name || '',
                }),
                cssClasses: ['menu-item'],
                icon: '➕',
                name: 'Add to current cluster',
              },
            ]
          : []

      const appListingMenuItems: MenuItemDef[] = [
        {
          action: removeAppsFromCluster(
            { apps: selectedApps, targetClusterId: clusterId, targetClusterName: clusterData?.name || '' },
            params.api
          ),
          cssClasses: ['menu-item'],
          icon: '❌',
          name: 'Remove from current cluster',
        },
        {
          cssClasses: ['menu-item'],
          icon: '❌',
          name: 'Remove from cluster',
          subMenu: getRemoveFromClusterMenuItems({ clusterIds, isActionMulti, selectedApps }, params.api),
        },
        {
          action: updateTags(selectedApps),
          cssClasses: ['menu-item'],
          icon: '🏷️',
          name: isActionMulti ? 'Add tags' : 'Update tags',
        },
        ...getAddTagActions({ apps: selectedApps, clusterId, isActionMulti }, params.api),
        {
          action: navigateToAppInfo(device_type, store_id, clusterId),
          cssClasses: ['menu-item'],
          icon: '➕',
          name: 'Add infos',
        },
      ]

      if (listingMode === 'cluster') return commonMenuItems.concat(appListingMenuItems)
      else if (listingMode === 'candidates') return commonMenuItems.concat(candidateMenuItems)
      return commonMenuItems
    },
    [
      addAppsToCluster,
      clusterId,
      clusterData?.name,
      getAddTagActions,
      getClustersMenuItems,
      getRemoveFromClusterMenuItems,
      listingMode,
      navigateToAppInfo,
      removeAppsFromCluster,
      searchCandidates,
      updateTags,
    ]
  )

  const handleCellContextMenu = async ({ api, event, node }: CellContextMenuEvent) => {
    const castedNode = node as RowNode
    const castedEvent = event as PointerEvent

    if (castedEvent) {
      api.showContextMenu({ rowNode: castedNode, value: getContextMenuItems, x: castedEvent.x, y: castedEvent.y })
    }
  }

  const handleAddAppToClusterSuccess = () => {
    gridRef?.current?.api?.refreshServerSide()
    setIsAddAppToClusterModalOpen(false)
  }

  const handleDeleteModalState = (isOpen: boolean) => () => setIsConfirmDeleteModalOpen(isOpen)

  const handleCreateClusterSuccess = () => {
    setIsCreateClusterModalOpen(false)
    displayToast('Cluster successfully created', 'success')
  }

  const handleDeleteConfirm = async () => {
    const selectedRows = gridRef?.current?.api?.getSelectedRows()
    if (selectedRows && selectedRows.length > 0) {
      try {
        const itemsToDelete = selectedRows.map(({ device_type, store_id }) => ({
          deviceType: device_type,
          storeId: store_id,
        }))

        await batchDeleteFromCluster({
          clusterId,
          items: itemsToDelete,
        })

        await queryClient.invalidateQueries({
          queryKey: clustersKeys.listAppsForClusters({
            clusterId,
            deviceType,
            filterPreset,
            tags,
          }),
        })

        await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.clusterStats(clusterId) })

        gridRef?.current?.api.applyServerSideTransaction({
          remove: selectedRows.map(({ store_id }) => ({ store_id })),
        })

        displayToast('App(s) successfully removed from cluster', 'success')
      } catch (e) {
        captureException(e)
        displayToast('App(s) could not be removed from cluster', 'error')
      } finally {
        setIsConfirmDeleteModalOpen(false)
      }
    }
  }

  const handleScreenshotModalClose = () => setAppForScreenshots(undefined)

  /** Render */

  return (
    <div className="cluster-details-view">
      {clusterData ? (
        <div className="cluster-details-view__header">
          <span className="cluster-details-view__header__name">{clusterData.name}</span>
          <div className="cluster-details-view__header__actions-container">
            <Button onClick={handleEditFeatures} outlined size="small">
              <MdOutlineSettings color="#ff5301" size={14} />
              Features Manager
            </Button>
            <Button onClick={handleEditClusterModalState(true)} outlined size="small">
              <MdEdit color="#ff5301" size={14} />
              Edit Cluster
            </Button>
          </div>
        </div>
      ) : (
        <div className="cluster-details-view__header-skeleton">
          <SkeletonLoader />
        </div>
      )}
      <MetricsBlock clusterId={clusterId} nbSelectedApps={selectedRows.length} />
      <ListingModeSwitchButton />
      {listingMode === 'cluster' ? (
        <AppsListingTable
          clusterId={clusterId}
          clusterTags={tagsData}
          getContextMenuItems={getContextMenuItems}
          gridRef={gridRef}
          onAddAppToCluster={handleAddAppToClusterModalState(true)}
          onContactIconClick={handleContactIconClick}
          onDeleteAppFromCluster={handleDeleteModalState(true)}
          onDeleteTag={handleRemoveTagFromCluster}
          onDisplayScreenshots={setAppForScreenshots}
          onRowSelectionChanged={handleRowSelectionChanged}
        />
      ) : null}
      {listingMode === 'candidates' ? (
        <ExploreTable
          clusterId={clusterId}
          getContextMenuItems={getContextMenuItems}
          gridRef={gridRef}
          onCellContextMenu={handleCellContextMenu}
          onContactIconClick={handleContactIconClick}
          onDisplayScreenshots={setAppForScreenshots}
          onRowSelectionChanged={handleRowSelectionChanged}
        />
      ) : null}
      <AddAppToClusterModal
        clusterId={clusterId}
        isOpen={isAddAppToClusterModalOpen}
        onClose={handleAddAppToClusterModalState(false)}
        onSuccess={handleAddAppToClusterSuccess}
      />
      {clusterData ? (
        <EditClusterModal
          clusterId={clusterId}
          editClusterParams={{
            description: clusterData.description,
            highVolumeThreshold: clusterData.high_volume_threshold,
            isGetKeywordsEnabled: clusterData.get_keywords_enabled,
            isGetReviewsEnabled: clusterData.get_reviews_enabled,
            name: clusterData.name,
            nbLeaders: clusterData.nb_leaders,
            status: clusterData.status,
            tags: clusterData.tags || [],
            userId: clusterData.user_id,
          }}
          isOpen={isEditModalOpen}
          onClose={handleEditClusterModalState(false)}
        />
      ) : null}
      <CompanyContactModal
        email={contactDetails.email}
        isOpen={isContactModalOpen}
        onClose={handleContactModalState(false)}
        phone={contactDetails.phone}
      />
      <CreateClusterModal
        clusterId={clusterId}
        clusterTags={tagsData}
        defaultApps={
          selectedRows.length > 0
            ? selectedRows.map(({ store_id }) => store_id || '')
            : newClusterFromAppStoreId
              ? [newClusterFromAppStoreId]
              : []
        }
        isOpen={isCreateClusterModalOpen}
        onClose={handleCreateClusterModalState(false)}
        onSuccess={handleCreateClusterSuccess}
      />
      {appsForTagsUpdate ? (
        <EditAppTagsModal
          apps={appsForTagsUpdate}
          clusterId={clusterId}
          clusterTags={tagsData}
          isOpen={isEditAppTagsModalOpen}
          onClose={handleEditAppTagsModalState(false)}
          onSuccess={handleEditAppTagsSuccess}
        />
      ) : null}
      <ModalDialog
        isOpen={isConfirmDeleteModalOpen}
        mainText={`Are you sure you want to remove ${
          selectedRows.length < 2 ? 'this app' : 'these apps'
        } from the current cluster?`}
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfirm}
      />
      <ScreenshotsModal app={appForScreenshots} isOpen={!!appForScreenshots} onClose={handleScreenshotModalClose} />
    </div>
  )
}

export default ClusterDetails
